import { useState, Fragment } from "react";
import { CompanyTableHeaderTitles } from "../../Constants";
import { ReactComponent as WrapIcon } from "../../../images/wrap.svg";
import { ReactComponent as UnwrapIcon } from "../../../images/unwrap.svg";

import UsersTable from "./UsersTable";

import LimitSelector from "../Selectors/LimitSelector";
import ActivationTypeSelector from "../Selectors/ActivationTypeSelector";
import MaxLicenseSetterComponent from "../MaxLicenseSetterComponent/MaxLicenseSetterComponent";

import ActionButtons from "../Buttons/ActionButtons";

export default function CompaniesTable({
  localization,
  language,
  companies,
  onUpdateCompany,
  onDeleteCompany,
  getCompanyID,
  onUpdateUser,
  onDeleteUser,
  getUserID,
}) {
  const [openedCompanyId, setOpenedCompanyId] = useState(null);
  const [companyDrafts, setCompanyDrafts] = useState({});

  const toggleCompany = (id) => {
    setOpenedCompanyId((current) => (current === id ? null : id));
  };

  // локальная проверка изменений на стороне Интерфейса пользователя
  const changeCompany = (company, patch) => {
    setCompanyDrafts((current) => {
      const nextDraft = { ...current[company.id], ...patch };
      const hasChanges = Object.entries(nextDraft).some(([key, value]) => value !== company[key]);

      if (!hasChanges) {
        const { [company.id]: removedDraft, ...remainingDrafts } = current;
        return remainingDrafts;
      }

      return { ...current, [company.id]: nextDraft };
    });
  };

  // фактическая отмена изменений, откат к Исходному состоянию на стороне Интерфейса
  const cancelCompanyChanges = (companyId) => {
    setCompanyDrafts((current) => {
      const { [companyId]: removedDraft, ...remainingDrafts } = current;
      return remainingDrafts;
    });
  };

  // обновляется Интерфейс пользователя и вызываем функции синхронизации с БД
  const saveCompanyChanges = async (companyId) => {
    const draft = companyDrafts[companyId];

    getCompanyID(companyId);

    if (!draft) return;

    const company = companies.find((item) => item.id === companyId);

    if (!company) return;

    const updatedCompany = {
      ...company,
      ...draft,
    };

    const isSaved = await onUpdateCompany(updatedCompany);

    if (isSaved) {
      cancelCompanyChanges(companyId);
    }
  };

  return (
    <div className="table">
      <table className="companies-table">
        <colgroup>
          {CompanyTableHeaderTitles.map((item, index) => (
            <col key={index} style={{ width: item.width }} />
          ))}
        </colgroup>
        <thead>
          <tr className="not-hovered">
            {CompanyTableHeaderTitles.map((item) => (
              <th key={item.title} scope="col" className="companies-table__title">
                <h2>{item.title ? localization[item.title][language] : ""}</h2>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {companies
            .filter((company) => company?.users.length > 0)
            .map((company) => {
              const draft = companyDrafts[company.id];
              const displayedCompany = draft ? { ...company, ...draft } : company;

              const activeUsersCount = company.users.filter((user) => user.activationStatus === 3).length;

              return (
                <Fragment key={company.id}>
                  <tr>
                    <td className="companies-table__cell">
                      <button
                        className={`wrap-table-button ${openedCompanyId === company.id ? "wrap-table-button--open" : ""}`}
                        type="button"
                        aria-label={`${localization[openedCompanyId === company.id ? "collapse" : "expand"][language]} ${company.domain}`}
                        aria-expanded={openedCompanyId === company.id}
                        onClick={() => toggleCompany(company.id)}
                      >
                        <WrapIcon className="wrap-table-button__icon wrap-table-button__icon-wrap" />

                        <UnwrapIcon className="wrap-table-button__icon wrap-table-button__icon-unwrap" />
                      </button>
                    </td>
                    <td className="companies-table__cell">{company.domain}</td>
                    <td className="companies-table__cell">
                      <ActivationTypeSelector
                        value={displayedCompany.activationType}
                        language={language}
                        onChange={(activationType) => changeCompany(company, { activationType })}
                      />
                    </td>
                    <td className="companies-table__cell">
                      <MaxLicenseSetterComponent
                        currentValue={displayedCompany.limit}
                        onChange={(limit) => changeCompany(company, { limit })}
                      />
                    </td>
                    <td className="companies-table__cell">
                      <LimitSelector
                        value={displayedCompany.licenseExpiresIn}
                        language={language}
                        onChange={(licenseExpiresIn) => changeCompany(company, { licenseExpiresIn })}
                      />
                    </td>
                    <td className="companies-table__cell">
                      <div className="companies-table__aria-title flex-row" title={localization.activeUsersCountTitle[language]}>
                        {`${company.users.length} / ${activeUsersCount}`}
                      </div>
                    </td>
                    <td className="companies-table__cell">
                      <ActionButtons
                        unitType={localization.companyUnit[language]}
                        unitId={company.id}
                        isDirty={Boolean(draft)}
                        onCancel={() => cancelCompanyChanges(company.id)}
                        onSave={() => saveCompanyChanges(company.id)}
                        onDelete={() => onDeleteCompany(company.id)}
                        localization={localization}
                        language={language}
                      />
                    </td>
                  </tr>

                  <tr className="not-hovered">
                    <td colSpan={CompanyTableHeaderTitles.length}>
                      <div className={`table__wrapper${openedCompanyId === company.id ? "--open" : ""}`}>
                        <UsersTable
                          localization={localization}
                          companyId={company.id}
                          userInfo={company.users}
                          language={language}
                          onUpdateUser={onUpdateUser}
                          onDeleteUser={onDeleteUser}
                          getUserID={getUserID}
                        />
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
